import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  getLanguageOption,
  getSelectableAgreementLanguages,
  isAgreementLanguageAvailable,
  resolveAgreementDocument,
} from '../../lib/agreementDocuments';
import { downloadAgreementPdf } from '../../lib/downloadAgreementPdf';
import { toast } from 'sonner';

/**
 * Shared language selector + download for Confidentiality / AML agreements.
 */
export default function AgreementLanguageToolbar({
  agreementKey,
  language,
  onLanguageChange,
  showDownload = true,
}) {
  const [downloading, setDownloading] = useState(false);
  const languages = getSelectableAgreementLanguages(agreementKey);
  const resolved = resolveAgreementDocument(agreementKey, language);
  const selectedLabel = getLanguageOption(language)?.label || language;
  const canDownload = showDownload && isAgreementLanguageAvailable(agreementKey, language);

  const handleDownload = async () => {
    if (downloading) return;
    if (!canDownload) {
      toast.error(resolved.message || 'Download is unavailable for the selected language.');
      return;
    }
    setDownloading(true);
    try {
      const result = await downloadAgreementPdf(agreementKey, language);
      toast.success(`Downloaded ${result.filename}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to download agreement');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`agreement-language-toolbar-${agreementKey}`}>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0 space-y-1.5">
          <Label htmlFor={`agreement-lang-${agreementKey}`} className="text-xs font-medium text-slate-700">
            Language
          </Label>
          <Select value={language} onValueChange={onLanguageChange}>
            <SelectTrigger
              id={`agreement-lang-${agreementKey}`}
              className="rounded-xl border-slate-200 bg-white"
              aria-label={`Select language for ${agreementKey}`}
              data-testid={`agreement-language-select-${agreementKey}`}
            >
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.label}
                  {!isAgreementLanguageAvailable(agreementKey, lang.code) ? ' (unavailable)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showDownload ? (
          <Button
            type="button"
            variant="outline"
            className="rounded-xl shrink-0"
            onClick={handleDownload}
            disabled={downloading || !canDownload}
            aria-label={`Download agreement in ${selectedLabel}`}
            data-testid={`agreement-download-${agreementKey}`}
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Downloading…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download
              </>
            )}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-slate-500" data-testid={`agreement-selected-lang-${agreementKey}`}>
        Selected language: <span className="font-medium text-slate-700">{selectedLabel}</span>
        {resolved.status === 'available' ? (
          <> · Document: <span className="font-medium text-slate-700">{resolved.downloadFilename}</span></>
        ) : null}
      </p>
      {resolved.status === 'unavailable' ? (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          role="alert"
          data-testid={`agreement-unavailable-${agreementKey}`}
        >
          <p>{resolved.message}</p>
          {resolved.showFallbackContent ? (
            <p className="mt-1">
              Showing the approved {resolved.fallbackLabel} version below for reading and acceptance.
              The selected language remains {selectedLabel}.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

AgreementLanguageToolbar.propTypes = {
  agreementKey: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  showDownload: PropTypes.bool,
};
